import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info, ExternalLink } from 'lucide-react';

const ResultsPanel = ({ results, handleShowDetails }) => {
    const { t } = useTranslation();

    return (
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-[#dee2e6] w-full">
                <h2 className="text-[#2c3e50] font-bold mb-5 text-2xl flex items-center gap-3">
                    <Info size={22} className="text-[#3498db]" />
                    {t('metrica:results.title')}
                </h2>
                
                <div className="flex flex-wrap gap-5">
                    {/* Skeletal */}
                    <div className="flex-1 min-w-[240px] bg-gradient-to-r from-[#f8f9fa] to-[#e9ecef] p-4 rounded-xl border border-[#ced4da]">
                        <h3 className="text-base font-bold text-[#2c3e50] mb-3">{t('metrica:results.categories.skeletal')}</h3>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#0066cc] font-bold text-base">{t('metrica:results.angles.SNA')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results ? `${results.sna.toFixed(1)}°` : '--'}</span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#cc3366] font-bold text-base">{t('metrica:results.angles.SNB')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results ? `${results.snb.toFixed(1)}°` : '--'}</span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#cc9900] font-bold text-base">{t('metrica:results.angles.ANB')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results ? `${results.anb.toFixed(1)}°` : '--'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Vertical */}
                    <div className="flex-1 min-w-[240px] bg-gradient-to-r from-[#f8f9fa] to-[#e9ecef] p-4 rounded-xl border border-[#ced4da]">
                        <h3 className="text-base font-bold text-[#2c3e50] mb-3">{t('metrica:results.categories.vertical')}</h3>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#6a5acd] font-bold text-base">{t('metrica:results.angles.ML_SNL')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results && results.mlSnlReal !== null ? `${results.mlSnlReal.toFixed(1)}°` : '--'}</span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#e67e22] font-bold text-base">{t('metrica:results.angles.NL_NSL')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results && results.nlNslReal !== null ? `${results.nlNslReal.toFixed(1)}°` : '--'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Calculated */}
                    <div className="flex-1 min-w-[240px] bg-gradient-to-r from-[#f8f9fa] to-[#e9ecef] p-4 rounded-xl border border-[#ced4da]">
                        <h3 className="text-base font-bold text-[#2c3e50] mb-3">{t('metrica:results.categories.calculated')}</h3>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#2e7d32] font-bold text-base italic">{t('metrica:results.angles.ML_SNL_CALC')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results && results.mlSnlCalc !== null ? `${results.mlSnlCalc.toFixed(1)}°` : '--'}</span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#2e7d32] font-bold text-base italic">{t('metrica:results.angles.NL_NSL_CALC')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results && results.nlNslCalc !== null ? `${results.nlNslCalc.toFixed(1)}°` : '--'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Indexes */}
                    <div className="flex-1 min-w-[240px] bg-gradient-to-r from-[#f8f9fa] to-[#e9ecef] p-4 rounded-xl border border-[#ced4da]">
                        <h3 className="text-base font-bold text-[#2c3e50] mb-3">{t('metrica:results.categories.indexes')}</h3>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#c62828] font-bold text-base">{t('metrica:results.angles.T1')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results && results.t1 !== null ? `${results.t1.toFixed(1)}°` : '--'}</span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#1565c0] font-bold text-base">{t('metrica:results.angles.T2')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results && results.t2 !== null ? `${results.t2.toFixed(1)}°` : '--'}</span>
                            </div>
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm">
                                <span className="text-[#f9a825] font-bold text-base">{t('metrica:results.angles.T3')}</span>
                                <span className="text-xl font-extrabold text-[#2c3e50]">{results && results.t3 !== null ? `${results.t3.toFixed(1)}°` : '--'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Classification & Prediction */}
                <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
                    
                    {results?.prediction && (
                        <div className="px-6 py-3 rounded-xl bg-gradient-to-br from-[#e3f2fd] to-[#bbdefb] border-2 border-[#2196f3] shadow-md text-center">
                            <span className="text-sm text-[#546e7a] uppercase tracking-wider font-bold block mb-1">
                                {t('metrica:results.prediction.label')}
                            </span>
                            <div className="flex items-center gap-3">
                                <span className="text-2xl font-extrabold text-[#1565c0]">{results.prediction}</span>
                                {results.predictionCode && (
                                    <button onClick={() => handleShowDetails(results.predictionCode)} className="flex items-center gap-2 px-4 py-2 bg-[#2196f3]/20 hover:bg-[#2196f3]/40 text-[#1565c0] rounded-lg text-sm font-bold transition-colors">
                                        <ExternalLink size={16} />
                                        {t('metrica:results.prediction.details')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
);
};

export default ResultsPanel;
